import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import {
  UserPlus, KeyRound, Mail, X, Search, Shield, ShieldOff,
  Trash2, Pencil, RefreshCw, Copy, Check,
} from "lucide-react";
import PortalLayout from "../../components/portal/PortalLayout";
import { portalApi } from "../../lib/portalApi";
import { usePortalAuth } from "../../contexts/PortalAuthContext";

const ROLE_OPTIONS = [
  // Client side
  { value: "client", label: "Client", group: "client_side" },
  { value: "client_project_manager", label: "Client Project Manager", group: "client_side" },
  { value: "client_interior_designer", label: "Client Interior Designer", group: "client_side" },
  { value: "client_associate", label: "Client Associate", group: "client_side" },
  { value: "client_subcontractor", label: "Client Subcontractor", group: "client_side" },
  { value: "client_supplier", label: "Client Supplier", group: "client_side" },
  { value: "consultant", label: "Client Consultant (legacy)", group: "client_side" },
  // Contractor side (Time Furniture internal)
  { value: "project_manager", label: "Project Manager", group: "contractor_side" },
  { value: "site_engineer", label: "Site Engineer", group: "contractor_side" },
  { value: "admin_staff", label: "Admin Staff", group: "contractor_side" },
  // External
  { value: "subcontractor", label: "Subcontractor", group: "external" },
  { value: "supplier", label: "Supplier", group: "external" },
  { value: "delivery_team", label: "Delivery Team", group: "external" },
  { value: "housekeeping_team", label: "Housekeeping / Cleaning Team", group: "external" },
];

const GROUP_LABELS = {
  client_side: "Client Side",
  contractor_side: "Contractor Side",
  external: "Subcontractors / Suppliers",
};

const STATUS_LABEL = {
  created: "Created",
  invite_sent: "Invite Sent",
  pending_first_login: "Pending First Login",
  active: "Active",
  password_reset_required: "Password Reset Required",
  disabled: "Disabled",
};
const STATUS_CLASS = {
  created: "bg-[#7A6B5F] text-[#FAF5EC]",
  invite_sent: "bg-[#D4A963] text-[#1C110C]",
  pending_first_login: "bg-[#C8A75D] text-[#1C110C]",
  active: "bg-[#6E1F2C] text-[#FAF5EC]",
  password_reset_required: "bg-[#A3141A] text-[#FAF5EC]",
  disabled: "bg-[#1C110C] text-[#FAF5EC]",
};

// Generate a strong password matching the policy hint shown to admin:
// upper, lower, digit, symbol, 12 chars.
function generatePassword(length = 14) {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%&*?+-";
  const all = upper + lower + digits + symbols;
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  const parts = [pick(upper), pick(lower), pick(digits), pick(symbols)];
  for (let i = parts.length; i < length; i++) parts.push(pick(all));
  // Fisher–Yates shuffle
  for (let i = parts.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [parts[i], parts[j]] = [parts[j], parts[i]];
  }
  return parts.join("");
}

function PasswordField({ value, onChange, testid, placeholder = "Strong temporary password" }) {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex gap-2">
      <input
        data-testid={testid}
        type={shown ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        minLength={8}
        required
        placeholder={placeholder}
        className="flex-1 bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 text-[#1C110C] focus:outline-none focus:border-[#6E1F2C] font-mono text-sm"
      />
      <button
        type="button"
        onClick={() => onChange(generatePassword())}
        title="Generate strong password"
        data-testid={`${testid}-generate`}
        className="px-3 py-2 border border-[#6E1F2C]/30 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors text-[10px] tracking-[0.2em] uppercase"
      >
        Generate
      </button>
      <button
        type="button"
        onClick={() => setShown((s) => !s)}
        title={shown ? "Hide password" : "Show password"}
        className="px-3 py-2 border border-[#6E1F2C]/30 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors text-[10px] tracking-[0.2em] uppercase"
      >
        {shown ? "Hide" : "Show"}
      </button>
      <button
        type="button"
        onClick={copy}
        disabled={!value}
        title="Copy password"
        className="px-3 py-2 border border-[#6E1F2C]/30 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors text-[10px] tracking-[0.2em] uppercase disabled:opacity-40"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  );
}

function CreateUserModal({ open, onClose, projects, onCreated }) {
  const empty = {
    name: "", email: "", company: "", phone: "",
    group: "client_side", role: "client",
    assigned_project_slug: "", assigned_scope: "",
    password: "", send_invite: true, include_password_in_email: true,
    force_password_change: true,
    daily_report_recipient: false, weekly_report_recipient: false,
    is_test: false,
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null); // last-created summary

  if (!open) return null;
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // When group changes, snap role to the first role in that group.
  const setGroup = (g) => {
    const firstRoleInGroup = ROLE_OPTIONS.find((r) => r.group === g);
    setForm((s) => ({ ...s, group: g, role: firstRoleInGroup?.value || s.role }));
  };

  const rolesInGroup = ROLE_OPTIONS.filter((r) => r.group === form.group);
  const isExternal = form.group === "external";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = {
        name: form.name, email: form.email, role: form.role,
        password: form.password,
        company: form.company || null, phone: form.phone || null,
        assigned_project_slug: form.assigned_project_slug || null,
        assigned_scope: form.assigned_scope || null,
        send_invite: form.send_invite,
        include_password_in_email: form.include_password_in_email,
        force_password_change: form.force_password_change,
        daily_report_recipient: form.daily_report_recipient,
        weekly_report_recipient: form.weekly_report_recipient,
        is_test: form.is_test,
      };
      const data = await portalApi.createUser(payload);
      setResult({ ...data, plainPassword: form.password });
      onCreated();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const closeAndReset = () => { setForm(empty); setResult(null); setErr(""); onClose(); };

  return (
    <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="bg-white w-full max-w-[640px] border border-[#6E1F2C]/20 p-7 md:p-9 max-h-[90vh] overflow-y-auto">
        {result ? (
          <div data-testid="portal-user-created-result">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">User created</p>
            <h2 className="font-serif-display text-2xl text-[#1C110C] mb-2">{result.user.name}</h2>
            <p className="text-sm text-[#3E2A22]">{result.user.email} · {ROLE_OPTIONS.find((r) => r.value === result.user.role)?.label}</p>
            <div className="bg-[#FAF5EC] border border-dashed border-[#C8A75D] p-5 my-5">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-1">Temporary password (shown only once)</p>
              <p className="font-mono text-lg text-[#1C110C] tracking-wider break-all">{result.plainPassword}</p>
              <p className="text-xs text-[#3E2A22] mt-3">
                {result.invite_sent
                  ? "An invitation email has been sent. You can copy this password for your records."
                  : "Save this password somewhere safe — it cannot be retrieved later. Share it with the user via your preferred channel."}
              </p>
            </div>
            <button
              onClick={closeAndReset}
              data-testid="portal-user-created-close"
              className="w-full bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F]"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="portal-new-user-form">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">Invite user</p>
                <h2 className="font-serif-display text-2xl text-[#1C110C]">Add portal user</h2>
              </div>
              <button type="button" onClick={closeAndReset} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
            </div>

            {/* Step 1 — pick the user group */}
            <div className="mb-5">
              <label className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">User group *</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {Object.entries(GROUP_LABELS).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setGroup(key)}
                    data-testid={`portal-new-user-group-${key}`}
                    className={`px-3 py-2.5 text-[10px] tracking-[0.22em] uppercase border transition-colors ${
                      form.group === key
                        ? "bg-[#6E1F2C] text-[#FAF5EC] border-[#6E1F2C]"
                        : "border-[#6E1F2C]/25 text-[#3E2A22] hover:border-[#6E1F2C]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <input data-testid="portal-new-user-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className={inputCls} />
              </Field>
              <Field label="Email" required>
                <input data-testid="portal-new-user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className={inputCls} />
              </Field>
              <Field label="Company">
                <input data-testid="portal-new-user-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Phone">
                <input data-testid="portal-new-user-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="+971-50-…" />
              </Field>
              <Field label={`Role within ${GROUP_LABELS[form.group]}`} required>
                <select data-testid="portal-new-user-role" value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls}>
                  {rolesInGroup.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </Field>
              <Field label="Assigned project">
                <select data-testid="portal-new-user-project" value={form.assigned_project_slug} onChange={(e) => set("assigned_project_slug", e.target.value)} className={inputCls}>
                  <option value="">— None (assign later) —</option>
                  {projects.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                </select>
              </Field>
              {isExternal && (
                <Field label="Assigned scope / package">
                  <input data-testid="portal-new-user-scope" value={form.assigned_scope} onChange={(e) => set("assigned_scope", e.target.value)} className={inputCls} placeholder="Marble, Joinery, MEP, Delivery, Cleaning…" />
                </Field>
              )}
            </div>

            {/* Report recipient flags */}
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-center gap-3 text-sm text-[#1C110C] cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="portal-new-user-daily-report"
                  checked={form.daily_report_recipient}
                  onChange={(e) => set("daily_report_recipient", e.target.checked)}
                  className="accent-[#6E1F2C]"
                />
                Daily report recipient
              </label>
              <label className="flex items-center gap-3 text-sm text-[#1C110C] cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="portal-new-user-weekly-report"
                  checked={form.weekly_report_recipient}
                  onChange={(e) => set("weekly_report_recipient", e.target.checked)}
                  className="accent-[#6E1F2C]"
                />
                Weekly report recipient
              </label>
            </div>

            <div className="mt-5">
              <label className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">Temporary password (min 8 chars)</label>
              <PasswordField value={form.password} onChange={(v) => set("password", v)} testid="portal-new-user-password" />
              <p className="text-[10px] text-[#7A6B5F] mt-2">User will be required to change this on first login.</p>
            </div>

            <div className="mt-5 space-y-3 border-t border-[#6E1F2C]/10 pt-5">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E1F2C]">Invitation email</p>
              <label className="flex items-center gap-3 text-sm text-[#1C110C] cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="portal-new-user-send-invite"
                  checked={form.send_invite}
                  onChange={(e) => set("send_invite", e.target.checked)}
                  className="accent-[#6E1F2C]"
                />
                Send invitation email to user
              </label>
              {form.send_invite && (
                <label className="flex items-center gap-3 text-sm text-[#1C110C] cursor-pointer pl-6">
                  <input
                    type="checkbox"
                    data-testid="portal-new-user-include-pw"
                    checked={form.include_password_in_email}
                    onChange={(e) => set("include_password_in_email", e.target.checked)}
                    className="accent-[#6E1F2C]"
                  />
                  Include temporary password in email
                </label>
              )}
              <p className="text-xs text-[#7A6B5F] pl-6">
                {form.send_invite
                  ? (form.include_password_in_email
                    ? "Option A — Send invite with temporary password."
                    : "Option B — Send invite without password (you'll share it via WhatsApp).")
                  : "Option C — Create user only (no email sent)."}
              </p>
            </div>

            {err && <p data-testid="portal-new-user-error" className="text-xs text-[#A3141A] mt-4">{err}</p>}

            <button
              type="submit"
              disabled={busy || !form.password}
              data-testid="portal-new-user-submit"
              className="w-full mt-6 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3.5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors disabled:opacity-60"
            >
              {busy ? "Creating…" : "Create user"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-2.5 text-[#1C110C] text-sm focus:outline-none focus:border-[#6E1F2C]";

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">
        {label}{required ? " *" : ""}
      </label>
      {children}
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [pw, setPw] = useState(generatePassword());
  const [sendEmail, setSendEmail] = useState(true);
  const [includePassword, setIncludePassword] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const res = await portalApi.resetUserPassword(user.id, {
        new_password: pw,
        send_email: sendEmail,
        include_password_in_email: includePassword,
      });
      setDone({ ...res, plainPassword: pw });
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto">
      <div className="bg-white w-full max-w-[520px] border border-[#6E1F2C]/20 p-7 md:p-9">
        {done ? (
          <div data-testid="portal-reset-pw-result">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">Password reset</p>
            <h2 className="font-serif-display text-2xl text-[#1C110C] mb-3">{user.name}</h2>
            <div className="bg-[#FAF5EC] border border-dashed border-[#C8A75D] p-5 my-4">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-1">New temporary password</p>
              <p className="font-mono text-lg text-[#1C110C] tracking-wider break-all">{done.plainPassword}</p>
              <p className="text-xs text-[#3E2A22] mt-3">
                {done.email_sent
                  ? "Email sent. The user must change this password on next login."
                  : "Share this password securely. The user must change it on next login."}
              </p>
            </div>
            <button onClick={onClose} data-testid="portal-reset-pw-close" className="w-full bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F]">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="portal-reset-pw-form">
            <div className="flex items-start justify-between mb-5">
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">Reset password</p>
                <h2 className="font-serif-display text-xl text-[#1C110C]">{user.name}</h2>
                <p className="text-xs text-[#7A6B5F]">{user.email}</p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
            </div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2">New temporary password</label>
            <PasswordField value={pw} onChange={setPw} testid="portal-reset-pw-input" />
            <p className="text-[10px] text-[#7A6B5F] mt-2 mb-4">User will be forced to change this on next login.</p>
            <label className="flex items-center gap-3 text-sm text-[#1C110C] cursor-pointer mb-2">
              <input type="checkbox" data-testid="portal-reset-pw-send-email" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="accent-[#6E1F2C]" />
              Send notification email to user
            </label>
            {sendEmail && (
              <label className="flex items-center gap-3 text-sm text-[#1C110C] cursor-pointer pl-6">
                <input type="checkbox" data-testid="portal-reset-pw-include-pw" checked={includePassword} onChange={(e) => setIncludePassword(e.target.checked)} className="accent-[#6E1F2C]" />
                Include new password in email
              </label>
            )}
            {err && <p className="text-xs text-[#A3141A] mt-3">{err}</p>}
            <button type="submit" disabled={busy || !pw} data-testid="portal-reset-pw-submit" className="w-full mt-5 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-60">
              {busy ? "Resetting…" : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ResendInviteModal({ user, onClose, onDone }) {
  const [setNewPw, setSetNewPw] = useState(false);
  const [pw, setPw] = useState(generatePassword());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOkMsg(""); setBusy(true);
    try {
      const res = await portalApi.resendInvite(user.id, {
        include_password: setNewPw,
        new_temporary_password: setNewPw ? pw : null,
      });
      setOkMsg(res.email_sent ? "Invitation email sent." : "Email not sent (check server logs).");
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="portal-resend-invite-form" className="bg-white w-full max-w-[480px] border border-[#6E1F2C]/20 p-7 md:p-9">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-2">Resend invitation</p>
            <h2 className="font-serif-display text-xl text-[#1C110C]">{user.name}</h2>
            <p className="text-xs text-[#7A6B5F]">{user.email}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
        </div>
        <label className="flex items-center gap-3 text-sm text-[#1C110C] cursor-pointer mb-3">
          <input type="checkbox" data-testid="portal-resend-set-new-pw" checked={setNewPw} onChange={(e) => setSetNewPw(e.target.checked)} className="accent-[#6E1F2C]" />
          Issue a NEW temporary password and include it
        </label>
        {setNewPw && (
          <div className="mb-4">
            <PasswordField value={pw} onChange={setPw} testid="portal-resend-pw-input" />
          </div>
        )}
        <p className="text-xs text-[#7A6B5F]">
          {setNewPw
            ? "User will be forced to change this new password on next login."
            : "Email will be re-sent without revealing the password (stored as hash). Use the option above if the user has forgotten theirs."}
        </p>
        {err && <p className="text-xs text-[#A3141A] mt-3">{err}</p>}
        {okMsg && <p className="text-xs text-[#6E1F2C] mt-3">{okMsg}</p>}
        <button type="submit" disabled={busy} data-testid="portal-resend-invite-submit" className="w-full mt-5 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-60">
          {busy ? "Sending…" : "Send invite email"}
        </button>
      </form>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name, role: user.role, company: user.company || "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await portalApi.updateUser(user.id, form);
      onSaved();
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1C110C]/60 backdrop-blur-sm flex items-center justify-center px-4">
      <form onSubmit={submit} data-testid="portal-edit-user-form" className="bg-white w-full max-w-[440px] border border-[#6E1F2C]/20 p-7 md:p-9">
        <div className="flex items-start justify-between mb-5">
          <h2 className="font-serif-display text-xl text-[#1C110C]">Edit user</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-[#7A6B5F] hover:text-[#1C110C]"><X size={18} /></button>
        </div>
        <Field label="Name"><input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} className={inputCls} required /></Field>
        <div className="h-3" />
        <Field label="Role">
          <select value={form.role} onChange={(e) => setForm((s) => ({ ...s, role: e.target.value }))} className={inputCls}>
            {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </Field>
        <div className="h-3" />
        <Field label="Company"><input value={form.company} onChange={(e) => setForm((s) => ({ ...s, company: e.target.value }))} className={inputCls} /></Field>
        {err && <p className="text-xs text-[#A3141A] mt-3">{err}</p>}
        <button type="submit" disabled={busy} data-testid="portal-edit-user-submit" className="w-full mt-5 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] disabled:opacity-60">
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </div>
  );
}

export default function UsersAccessPage() {
  const { user } = usePortalAuth();
  const [users, setUsers] = useState(null);
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [hideTest, setHideTest] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resendTarget, setResendTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const reload = useCallback(async () => {
    const [u, p] = await Promise.all([
      portalApi.listUsers(undefined, true),
      portalApi.listProjects(),
    ]);
    setUsers(u.users || []);
    setProjects(p.projects || []);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    if (!users) return null;
    let list = users;
    if (groupFilter !== "all") list = list.filter((u) => (u.group || "") === groupFilter);
    if (hideTest) {
      list = list.filter((u) =>
        !u.is_test
        && !/@example\.com$/i.test(u.email)
        && !/^(qa\.|test\.|test\+|pm\.test|site\.test|client\.test|sara\.test)/i.test(u.email)
      );
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((u) =>
        u.name.toLowerCase().includes(q)
        || u.email.toLowerCase().includes(q)
        || (u.company || "").toLowerCase().includes(q)
        || (u.role || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [users, query, groupFilter, hideTest]);

  const toggleDisable = async (u) => {
    const next = u.status === "disabled" ? "active" : "disabled";
    try {
      await portalApi.updateUser(u.id, { status: next });
      reload();
    } catch (e) { alert(e.message); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await portalApi.deleteUser(u.id);
      reload();
    } catch (e) { alert(e.message); }
  };

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/projects" replace />;

  return (
    <PortalLayout
      title="Users & Access"
      breadcrumbs={[{ label: "Admin Dashboard", to: "/projects/admin" }, { label: "Users & Access" }]}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-[#3E2A22]">
          {users === null ? "Loading…" : `${filtered ? filtered.length : 0} of ${users.length} portal user${users.length === 1 ? "" : "s"}`}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#3E2A22] cursor-pointer">
            <input
              type="checkbox"
              data-testid="portal-users-hide-test"
              checked={hideTest}
              onChange={(e) => setHideTest(e.target.checked)}
              className="accent-[#6E1F2C]"
            />
            Hide QA / test users
          </label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A6B5F]" />
            <input
              data-testid="portal-users-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users…"
              className="pl-9 pr-3 py-2.5 bg-white border border-[#6E1F2C]/20 text-sm text-[#1C110C] focus:outline-none focus:border-[#6E1F2C]"
            />
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            data-testid="portal-users-new-btn"
            className="inline-flex items-center gap-2 bg-[#6E1F2C] text-[#FAF5EC] px-4 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#56151F]"
          >
            <UserPlus size={14} /> Add user
          </button>
        </div>
      </div>

      {/* Group filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="portal-users-group-filter">
        {[
          { k: "all", label: "All groups" },
          { k: "client_side", label: GROUP_LABELS.client_side },
          { k: "contractor_side", label: GROUP_LABELS.contractor_side },
          { k: "external", label: GROUP_LABELS.external },
        ].map((g) => (
          <button
            key={g.k}
            type="button"
            onClick={() => setGroupFilter(g.k)}
            data-testid={`portal-users-group-${g.k}`}
            className={`px-3 py-1.5 text-[10px] tracking-[0.22em] uppercase border transition-colors ${
              groupFilter === g.k
                ? "bg-[#6E1F2C] text-[#FAF5EC] border-[#6E1F2C]"
                : "border-[#6E1F2C]/25 text-[#3E2A22] hover:border-[#6E1F2C]"
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>

      <PurgeQaBanner reload={reload} />

      <div className="bg-white border border-[#6E1F2C]/15 overflow-x-auto" data-testid="portal-users-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-[0.2em] uppercase text-[#7A6B5F] border-b border-[#6E1F2C]/10">
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Group</th>
              <th className="text-left px-5 py-3">Role</th>
              <th className="text-left px-5 py-3">Company / Phone</th>
              <th className="text-left px-5 py-3">Projects</th>
              <th className="text-left px-5 py-3">Reports</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered === null ? null : filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-[#7A6B5F]">No users found.</td></tr>
            ) : filtered.map((u) => (
              <tr key={u.id} data-testid={`portal-user-row-${u.id}`} className="border-b border-[#6E1F2C]/10 hover:bg-[#FAF5EC]/40">
                <td className="px-5 py-4">
                  <p className="text-[#1C110C]">{u.name}</p>
                  <p className="text-[10px] text-[#7A6B5F]">{u.email}</p>
                  {u.is_test && <span className="text-[9px] tracking-[0.18em] uppercase text-[#A3141A]">QA / TEST</span>}
                </td>
                <td className="px-5 py-4">
                  <span className="text-[9px] tracking-[0.18em] uppercase text-[#6E1F2C] border border-[#6E1F2C]/30 px-2 py-0.5">
                    {GROUP_LABELS[u.group] || "—"}
                  </span>
                </td>
                <td className="px-5 py-4 text-[#3E2A22]">{u.role_label || ROLE_OPTIONS.find((r) => r.value === u.role)?.label || u.role}</td>
                <td className="px-5 py-4 text-[#3E2A22]">
                  <p>{u.company || "—"}</p>
                  {u.phone && <p className="text-[10px] text-[#7A6B5F]">{u.phone}</p>}
                </td>
                <td className="px-5 py-4 text-[#3E2A22]">
                  {(u.assigned_projects || []).length === 0 ? (
                    <span className="text-[10px] text-[#7A6B5F] italic">None</span>
                  ) : (
                    <ul className="space-y-1">
                      {(u.assigned_projects || []).slice(0, 3).map((p) => (
                        <li key={p.id} className="text-xs">
                          {p.name}{p.scope ? <span className="text-[#7A6B5F]"> · {p.scope}</span> : null}
                        </li>
                      ))}
                      {(u.assigned_projects || []).length > 3 && (
                        <li className="text-[10px] text-[#7A6B5F]">+{u.assigned_projects.length - 3} more</li>
                      )}
                    </ul>
                  )}
                </td>
                <td className="px-5 py-4">
                  <div className="flex flex-col gap-1 text-[10px] tracking-[0.18em] uppercase">
                    <span className={u.daily_report_recipient ? "text-[#6E1F2C]" : "text-[#7A6B5F]/50"}>
                      {u.daily_report_recipient ? "✓ Daily" : "— Daily"}
                    </span>
                    <span className={u.weekly_report_recipient ? "text-[#6E1F2C]" : "text-[#7A6B5F]/50"}>
                      {u.weekly_report_recipient ? "✓ Weekly" : "— Weekly"}
                    </span>
                  </div>
                </td>
                <td className="px-5 py-4">
                  <span className={`text-[9px] tracking-[0.2em] uppercase px-2 py-1 ${STATUS_CLASS[u.status] || "bg-[#7A6B5F] text-[#FAF5EC]"}`}>
                    {STATUS_LABEL[u.status] || u.status}
                  </span>
                  {u.force_password_change && (
                    <p className="text-[9px] text-[#A3141A] mt-1 tracking-wider uppercase">↻ must change password</p>
                  )}
                </td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <ActionBtn testid={`portal-user-edit-${u.id}`} title="Edit" onClick={() => setEditTarget(u)}><Pencil size={13} /></ActionBtn>
                    <ActionBtn testid={`portal-user-reset-${u.id}`} title="Reset password" onClick={() => setResetTarget(u)}><KeyRound size={13} /></ActionBtn>
                    <ActionBtn testid={`portal-user-resend-${u.id}`} title="Resend invite" onClick={() => setResendTarget(u)}><Mail size={13} /></ActionBtn>
                    <ActionBtn testid={`portal-user-toggle-${u.id}`} title={u.status === "disabled" ? "Re-activate" : "Disable"} onClick={() => toggleDisable(u)}>
                      {u.status === "disabled" ? <Shield size={13} /> : <ShieldOff size={13} />}
                    </ActionBtn>
                    {u.id !== user.id && (
                      <ActionBtn testid={`portal-user-delete-${u.id}`} title="Delete" danger onClick={() => remove(u)}><Trash2 size={13} /></ActionBtn>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        projects={projects}
        onCreated={reload}
      />
      {resetTarget && <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} onDone={reload} />}
      {resendTarget && <ResendInviteModal user={resendTarget} onClose={() => setResendTarget(null)} onDone={reload} />}
      {editTarget && <EditUserModal user={editTarget} onClose={() => setEditTarget(null)} onSaved={reload} />}
    </PortalLayout>
  );
}

function ActionBtn({ children, onClick, title, danger, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-2 border transition-colors ${
        danger
          ? "border-[#A3141A]/40 text-[#A3141A] hover:bg-[#A3141A] hover:text-white"
          : "border-[#6E1F2C]/25 text-[#6E1F2C] hover:bg-[#6E1F2C] hover:text-[#FAF5EC]"
      }`}
    >
      {children}
    </button>
  );
}

function PurgeQaBanner({ reload }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const run = async () => {
    if (!window.confirm(
      "This will permanently delete every project whose code starts with 'QA-' or 'TEST-' " +
      "(including ALL their members, updates, photos, files, reports and timeline data).\n\n" +
      "It will NOT touch real projects.\n\nProceed?"
    )) return;
    setBusy(true); setErr("");
    try {
      const r = await portalApi.purgeQaData();
      setResult(r);
      reload();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="mb-6 border border-[#A3141A]/25 bg-[#FAF5EC] p-4 flex flex-wrap items-center justify-between gap-3" data-testid="portal-purge-banner">
      <div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#A3141A] mb-1">Pre-production cleanup</p>
        <p className="text-xs text-[#3E2A22]">
          Remove all projects whose code starts with <code>QA-</code> or <code>TEST-</code> before going live. Real projects are unaffected.
        </p>
        {result && (
          <p className="text-[10px] text-[#3F6E2C] mt-1" data-testid="portal-purge-result">
            Removed {result.deleted_projects.length} project{result.deleted_projects.length === 1 ? "" : "s"}.
          </p>
        )}
        {err && <p className="text-[10px] text-[#A3141A] mt-1">{err}</p>}
      </div>
      <button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid="portal-purge-btn"
        className="border border-[#A3141A] text-[#A3141A] px-4 py-2 text-[10px] tracking-[0.28em] uppercase hover:bg-[#A3141A] hover:text-white transition-colors disabled:opacity-50"
      >
        {busy ? "Purging…" : "Purge QA / Test data"}
      </button>
    </div>
  );
}
